import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion, useSpring } from "framer-motion";

export function SiteLoader() {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const timer = window.setTimeout(() => setVisible(false), 1850);
    return () => window.clearTimeout(timer);
  }, []);

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.55, ease: "easeOut" } }}
          className="loader-shell fixed inset-0 z-[90] flex items-center justify-center"
          data-testid="site-loader"
        >
          <div className="loader-grid absolute inset-0" />
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.72, ease: [0.22, 1, 0.36, 1] }}
            className="loader-copy-wrap relative px-6 text-center"
          >
            <motion.div
              initial={{ opacity: 0, scaleX: 0.4 }}
              animate={{ opacity: [0, 0.95, 0.38], scaleX: [0.4, 1, 1.08] }}
              transition={{ duration: 1.25, ease: [0.22, 1, 0.36, 1] }}
              className="loader-line-reveal absolute left-1/2 top-1/2 h-px w-[min(88vw,720px)] -translate-x-1/2 -translate-y-1/2"
              data-testid="site-loader-line-reveal"
            />
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.65, delay: 0.18 }}
              className="relative z-10"
            >
              <p className="font-mono-display text-[10px] uppercase tracking-[0.58em] text-white/[0.42] sm:text-xs">Equinox</p>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, letterSpacing: "0.2em" }}
              animate={{ opacity: 1, letterSpacing: "0.02em" }}
              transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
              className="loader-title relative z-10 mt-5 max-w-[18ch] font-display text-4xl text-white sm:text-5xl lg:text-6xl"
              data-testid="site-loader-title"
            >
              Equinox Marketing Agency Since 2019
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="relative z-10 mx-auto mt-5 max-w-xl text-xs uppercase tracking-[0.32em] text-white/[0.34]"
              data-testid="site-loader-subtitle"
            >
              Intelligent acquisition systems • automation • retention infrastructure
            </motion.p>
            <div className="loader-track relative z-10 mx-auto mt-8 w-[220px] overflow-hidden rounded-full">
              <motion.div
                initial={{ x: "-100%" }}
                animate={{ x: ["-100%", "0%", "100%"] }}
                transition={{ duration: 1.45, repeat: Infinity, ease: "easeInOut" }}
                className="loader-bar h-2 w-1/2 rounded-full"
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export function CursorAura({ pointerX, pointerY }) {
  const ringX = useSpring(pointerX, { stiffness: 240, damping: 26 });
  const ringY = useSpring(pointerY, { stiffness: 240, damping: 26 });
  const dotX = useSpring(pointerX, { stiffness: 540, damping: 34 });
  const dotY = useSpring(pointerY, { stiffness: 540, damping: 34 });

  return (
    <>
      <motion.div
        className="cursor-ring hidden lg:block"
        style={{ x: ringX, y: ringY }}
        data-testid="cursor-ring"
      />
      <motion.div
        className="cursor-dot hidden lg:block"
        style={{ x: dotX, y: dotY }}
        data-testid="cursor-dot"
      />
    </>
  );
}

export function TiltCard({ children, className = "", testId = undefined }) {
  const rotateX = useSpring(0, { stiffness: 220, damping: 18 });
  const rotateY = useSpring(0, { stiffness: 220, damping: 18 });
  const [hovered, setHovered] = useState(false);

  const glow = useMemo(
    () =>
      hovered
        ? "0 28px 70px rgba(0,0,0,0.42), 0 0 34px rgba(255,255,255,0.06), 0 0 24px rgba(37,99,235,0.12)"
        : "0 18px 60px rgba(0,0,0,0.26)",
    [hovered],
  );

  const onMove = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const px = (event.clientX - rect.left) / rect.width;
    const py = (event.clientY - rect.top) / rect.height;
    rotateY.set((px - 0.5) * 12);
    rotateX.set((0.5 - py) * 10);
  };

  const onLeave = () => {
    setHovered(false);
    rotateX.set(0);
    rotateY.set(0);
  };

  return (
    <motion.div
      onMouseMove={onMove}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={onLeave}
      style={{ rotateX, rotateY, transformPerspective: 1400, boxShadow: glow }}
      className={className}
      data-testid={testId}
    >
      {children}
    </motion.div>
  );
}